import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import {
	DOCUSIGN_DOCUMENT_TYPE_MAPPING,
	ProjectDocumentForSignatureTypeKeys,
} from "@dune/types";
import { DOCUSIGN_RECIPIENT_ID } from "@dune/vars";
import { isAxiosError } from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
	useGetEnvelopeTracking,
	useGetSigningUrl,
} from "../../../../api/mutations/documents.mutations";
import { usePropositionActiveEnvelope } from "../../../../api/queries/propositions.queries";

interface SignDocumentDuneModalProps {
	type: ProjectDocumentForSignatureTypeKeys;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	propositionId: number;
	onSuccess?: () => void;
}

const SignDocumentDuneModal: React.FC<SignDocumentDuneModalProps> = ({
	type,
	open,
	onOpenChange,
	propositionId,
	onSuccess,
}) => {
	const labels = DOCUSIGN_DOCUMENT_TYPE_MAPPING[type].labels;
	const feminine = DOCUSIGN_DOCUMENT_TYPE_MAPPING[type].feminine;

	const [signingUrl, setSigningUrl] = useState<string | null>(null);
	const [signatureCompleted, setSignatureCompleted] = useState(false);

	const {
		data: activeEnvelope,
		isLoading: activeLoading,
		error: activeError,
	} = usePropositionActiveEnvelope(propositionId, type);

	const envelopeId = activeEnvelope?.envelopeId ?? null;

	const getSigningUrlMutation = useGetSigningUrl();
	const {
		data: tracking,
		isLoading: trackingLoading,
		refetch: refetchTracking,
	} = useGetEnvelopeTracking(envelopeId || "");

	const isDuneSigned =
		tracking?.recipients?.find(
			(r) => r.recipientId === DOCUSIGN_RECIPIENT_ID.DUNE,
		)?.status === "SIGNED" ||
		tracking?.recipients?.find(
			(r) => r.recipientId === DOCUSIGN_RECIPIENT_ID.DUNE,
		)?.status === "COMPLETED";

	useEffect(() => {
		if (!open) {
			setSigningUrl(null);
			setSignatureCompleted(false);
		}
	}, [open]);

	useEffect(() => {
		if (activeError && open) {
			toast.error("Erreur lors de la récupération de l'enveloppe active");
			onOpenChange(false);
		}
	}, [activeError, open, onOpenChange]);

	useEffect(() => {
		if (open && !activeLoading && !envelopeId) {
			toast.error("Aucune enveloppe de signature active trouvée");
			onOpenChange(false);
		}
	}, [open, activeLoading, envelopeId, onOpenChange]);

	const handleStartSigning = async () => {
		if (!envelopeId || !tracking) return;

		const duneRecipient = tracking.recipients.find(
			(r) => r.recipientId === DOCUSIGN_RECIPIENT_ID.DUNE,
		);
		if (!duneRecipient) {
			toast.error("Aucun signataire DUNE trouvé");
			return;
		}

		try {
			const response = await getSigningUrlMutation.mutateAsync({
				envelopeId: envelopeId,
				recipientId: duneRecipient.recipientId,
			});

			if (response.signingUrl) {
				setSigningUrl(response.signingUrl);
			} else {
				throw new Error("URL de signature non reçue");
			}
		} catch (error: unknown) {
			let message: string | undefined;
			if (isAxiosError(error)) {
				const data = error.response?.data as unknown;
				if (data && typeof data === "object" && "message" in data) {
					const m = (data as Record<string, unknown>).message;
					if (typeof m === "string") message = m;
				}
			}
			if (typeof message === "string" && message.includes("déjà été signé")) {
				toast.error(
					`${labels.article.indefinite} a déjà été signé${feminine ? "e" : ""} par DUNE`,
				);
				refetchTracking();
			} else if (
				typeof message === "string" &&
				message.includes(
					`déjà été signé${feminine ? "e" : ""} par toutes les parties`,
				)
			) {
				toast.error(
					`${labels.article.definite} a déjà été signé${feminine ? "e" : ""} par toutes les parties`,
				);
				refetchTracking();
			} else if (typeof message === "string") {
				toast.error(message);
			} else {
				toast.error(
					"Erreur lors de la génération de l'URL de signature. Veuillez réessayer.",
				);
			}
		}
	};

	const handleSigningComplete = useCallback(() => {
		setSigningUrl(null);
		setSignatureCompleted(true);
		toast.success("Signature terminée avec succès!");
		setTimeout(() => {
			onOpenChange(false);
			onSuccess?.();
		}, 2000);
	}, [onOpenChange, onSuccess]);

	useEffect(() => {
		if (!signingUrl) return;
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;
			if (event.data?.type === "docusign_signing_complete") {
				handleSigningComplete();
			}
		};
		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [signingUrl, handleSigningComplete]);

	const isLoading = activeLoading || trackingLoading;
	const canStartSigning = !isLoading && envelopeId && tracking;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className={`${signingUrl ? "sm:max-w-[95vw] h-[95vh] max-h-[95vh] p-0 flex flex-col" : "sm:max-w-lg"}`}
			>
				{signatureCompleted ? (
					<>
						<DialogHeader>
							<DialogTitle>Proposition signée avec succès</DialogTitle>
						</DialogHeader>
						<div className="flex flex-col items-center justify-center py-12">
							<CheckCircle2 className="h-16 w-16 text-green-600 mb-6" />
							<h3 className="text-xl font-semibold text-gray-900 mb-2">
								Signature terminée !
							</h3>
							<p className="text-gray-600 text-center max-w-md">
								{labels.article.definite} a été signé{feminine ? "e" : ""} avec
								succès par DUNE. Le document signé sera disponible dans quelques
								instants.
							</p>
						</div>
					</>
				) : !signingUrl ? (
					<>
						<DialogHeader>
							<DialogTitle>Signature {labels.preposition} par DUNE</DialogTitle>
							<DialogDescription>
								{isLoading
									? "Chargement des informations de signature..."
									: `Cliquez sur le bouton ci-dessous pour commencer la signature électronique ${labels.preposition}`}
							</DialogDescription>
						</DialogHeader>
						<div className="flex flex-col items-center justify-center py-6">
							{isLoading ? (
								<Loader2 className="h-8 w-8 animate-spin text-gray-500" />
							) : isDuneSigned ? (
								<>
									<CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
									<p className="text-gray-600 text-center max-w-md">
										{labels.article.indefinite} a déjà été signé
										{feminine ? "e" : ""} par DUNE.
									</p>
								</>
							) : (
								<>
									<p className="text-gray-600 text-center max-w-md mb-6">
										La signature électronique vous permet de signer{" "}
										{labels.preposition} de manière sécurisée directement depuis
										cette interface.
									</p>
									<Button
										onClick={handleStartSigning}
										disabled={
											getSigningUrlMutation.isPending ||
											!canStartSigning ||
											isDuneSigned
										}
										size="lg"
									>
										{getSigningUrlMutation.isPending ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												Chargement...
											</>
										) : (
											"Commencer la signature"
										)}
									</Button>
								</>
							)}
						</div>
					</>
				) : (
					<>
						<DialogHeader className="p-4 pb-2 border-b">
							<DialogTitle className="text-lg">
								Signature {labels.preposition} par DUNE
							</DialogTitle>
							<DialogDescription className="text-sm">
								Veuillez signer le document dans l'interface ci-dessous. Une
								fois terminé, fermez cette fenêtre.
							</DialogDescription>
						</DialogHeader>
						<div className="flex-1 relative overflow-hidden">
							<iframe
								src={signingUrl ?? undefined}
								className="absolute inset-0 w-full h-full border-0"
								title="DocuSign Signature"
								allow="microphone; camera"
							/>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default SignDocumentDuneModal;
